import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation } from "@apollo/client";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { CONFIRM_STRIPE_CHECKOUT_SESSION } from "../../../graphql/queries";

const isPricingRoute = (path) =>
    String(path || "").split("?")[0].split("#")[0] === "/biz/pricing";

const getLastBizRouteBeforePricing = () => {
    try {
        const stored = localStorage.getItem("lastBizRouteBeforePricing") || "";
        const normalized = String(stored || "").split("?")[0].split("#")[0];
        if (!stored || normalized === "/biz/pricing") return "";
        return stored;
    } catch (_error) {
        return "";
    }
};

const readNavigationPath = (key) => {
    const raw = localStorage.getItem(key);
    if (!raw) return "";
    try {
        const parsed = JSON.parse(raw);
        const path = typeof parsed?.path === "string" ? parsed.path : "";
        const createdAt = Number(parsed?.createdAt || 0);
        const isFresh =
            createdAt > 0 && Date.now() - createdAt < 2 * 60 * 60 * 1000;
        return isFresh ? path : "";
    } catch (_error) {
        return "";
    }
};

const BillingSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const wasPlanUpdated = String(searchParams.get("updated") || "") === "1";
    const confirmationStarted = useRef(false);
    const [
        confirmCheckout,
        {
            data: confirmationData,
            loading: confirming,
            error: confirmationError,
        },
    ] = useMutation(CONFIRM_STRIPE_CHECKOUT_SESSION);
    const confirmedSubscription =
        confirmationData?.confirmStripeCheckoutSession;

    const runConfirmation = useCallback(async () => {
        if (!sessionId) return;
        confirmationStarted.current = true;
        await confirmCheckout({ variables: { sessionId } });
    }, [confirmCheckout, sessionId]);

    useEffect(() => {
        if (!sessionId || wasPlanUpdated || confirmationStarted.current) return;
        runConfirmation().catch(() => {
            // Apollo exposes the error below and lets the customer retry.
        });
    }, [runConfirmation, sessionId, wasPlanUpdated]);

    const confirmationComplete =
        wasPlanUpdated || Boolean(confirmedSubscription);
    const referenceId = sessionId
        ? `${sessionId.slice(0, 12)}...${sessionId.slice(-6)}`
        : "";

    const returnPath = useMemo(
        () =>
            readNavigationPath("pendingUpgradeNavigation") ||
            readNavigationPath("upgradeCallerNavigation"),
        []
    );

    const normalizedReturnPath = isPricingRoute(returnPath)
        ? getLastBizRouteBeforePricing() || "/biz/business"
        : returnPath;
    const resolvedReturnPath =
        normalizedReturnPath || (wasPlanUpdated ? "/biz" : "");

    const clearUpgradeNavigation = () => {
        localStorage.removeItem("pendingUpgradeNavigation");
        localStorage.removeItem("upgradeCallerNavigation");
    };

    const handleBackToPrevious = () => {
        clearUpgradeNavigation();
        if (resolvedReturnPath) {
            navigate(resolvedReturnPath, { replace: true });
            return;
        }
        if (typeof window !== "undefined" && window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate("/biz", { replace: true });
    };

    const handleGoDashboard = () => {
        clearUpgradeNavigation();
        navigate("/biz/dashboard", { replace: true });
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    maxWidth: 520,
                    width: "100%",
                    p: 4,
                    textAlign: "center",
                }}
            >
                {confirming ? (
                    <CircularProgress size={56} sx={{ mb: 2 }} />
                ) : (
                    <CheckCircleIcon
                        sx={{
                            fontSize: 64,
                            color: confirmationComplete
                                ? "success.main"
                                : "text.disabled",
                            mb: 2,
                        }}
                    />
                )}

                <Typography variant="h5" fontWeight={700} gutterBottom>
                    {confirming
                        ? "Confirming your subscription..."
                        : confirmationComplete
                            ? "Subscription Successful"
                            : "Subscription confirmation needed"}
                </Typography>

                {confirmationError ? (
                    <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                        Stripe checkout completed, but we could not refresh your
                        local plan yet. Please retry confirmation.
                    </Alert>
                ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {confirmationComplete
                            ? "Your subscription is active and the local plan has been updated."
                            : "Please wait while we verify the completed Stripe checkout."}
                    </Typography>
                )}

                {confirmationComplete && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        You can now access the features included in your selected
                        plan.
                    </Typography>
                )}

                {referenceId && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 3 }}
                    >
                        Reference ID: {referenceId}
                    </Typography>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {confirmationError && sessionId && (
                        <Button
                            onClick={() => runConfirmation().catch(() => {})}
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={confirming}
                        >
                            Retry Confirmation
                        </Button>
                    )}
                    <Button
                        onClick={handleBackToPrevious}
                        variant="outlined"
                        size="large"
                        fullWidth
                        disabled={confirming}
                    >
                        Back to Previous Page
                    </Button>
                    <Button
                        onClick={handleGoDashboard}
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={confirming}
                    >
                        Go to Dashboard
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default BillingSuccess;
